/* 기호 번역층. 문항의 표기(그리스 문자, 아래첨자)와 mathcore 식 사이를 한 곳에서 잇는다.
 * 기호의 이름과 뜻은 계산 엔진이 식에서 읽은 것이 정본이고, 글자 목록을 따로 두지 않는다.
 * mathcore 는 영문 한 글자만 식별자로 읽으므로 기호는 값으로 대입해 넘긴다(expr.ts 의 readCurve) */
import type { ComputeEngine } from '@cortex-js/compute-engine';
import { NOT_SYMBOLS, canonicalName, latexOf, newEngine } from './engine';
import { unwrapMath } from './expr';
import { plainFormulas, splitSegments } from './segments';

export { canonicalName, latexOf };

/** 표준 LaTeX 그리스 명령 이름. 엔진이 기호로 읽는지 가려내는 데만 쓴다 */
const GREEK_COMMANDS = [
  'alpha', 'beta', 'gamma', 'delta', 'epsilon', 'zeta', 'eta', 'theta', 'iota', 'kappa', 'lambda', 'mu', 'nu', 'xi', 'omicron', 'rho', 'sigma', 'tau', 'upsilon', 'phi', 'chi', 'psi', 'omega',
  'varepsilon', 'vartheta', 'varpi', 'varrho', 'varsigma', 'varphi',
  'Gamma', 'Delta', 'Theta', 'Lambda', 'Xi', 'Pi', 'Sigma', 'Upsilon', 'Phi', 'Psi', 'Omega',
];

/** 프롬프트용 기호 목록: alpha (\alpha), t */
export function describeSymbols(names: readonly string[]): string[] {
  return names.map((n) => { const l = latexOf(n); return l === n ? n : `${n} (${l})`; });
}

/** 식 하나에서 값이 필요한 기호. 변수와 함수 이름은 뺀다 */
export function unknownsOf(latex: string, engine: ComputeEngine): string[] {
  try { return engine.parse(unwrapMath(latex)).unknowns.filter((u) => !NOT_SYMBOLS.includes(u)); } catch { return []; }
}

/** 문항의 식 조각들이 쓰는 기호. 개체 이름(점, 함수)은 objectNames 로 빼므로 매개변수와 겹치지 않는다 */
export function problemSymbols(problem: string, objectNames: readonly string[]): string[] {
  const engine = newEngine();
  const pieces = splitSegments(problem).map((s) => s.text);
  const out: string[] = [];
  for (const piece of pieces.length ? pieces : plainFormulas(problem, () => true)) {
    for (const u of unknownsOf(piece, engine)) if (!objectNames.includes(u) && !out.includes(u)) out.push(u);
  }
  return out;
}

/** 고를 수 있는 식 목록이 쓰는 기호 전부. 도구 선택이 값을 선언할 수 있는 이름의 범위다 */
export function givenSymbols(given: readonly string[]): string[] {
  const engine = newEngine();
  const out: string[] = [];
  for (const g of given) for (const u of unknownsOf(g, engine)) if (!out.includes(u)) out.push(u);
  return out;
}

/** 식에 쓰인 그리스 명령 가운데 엔진이 기호로 읽지 않는 것(상수나 함수로 읽는다). 이 기호는 값을 줄 수 없다. \pi 는 mathcore 가 읽는다 */
export function notationIssues(text: string): string[] {
  const out: string[] = [];
  for (const name of GREEK_COMMANDS) {
    if (!String(text ?? '').includes(`\\${name}`) || canonicalName(name) !== null) continue;
    out.push(`\\${name} 는 엔진이 기호가 아닌 것(상수나 함수)으로 읽어 값을 줄 수 없다. 식에서 수로 바꾼다`);
  }
  return out;
}

/** JSON 이스케이프 \b \f \n \r \t 가 삼킨 LaTeX 명령의 역슬래시와 첫 글자. 문법 강제 출력은 이 다섯 이스케이프를 허용하므로 모델을 가리지 않고 생긴다 */
const SWALLOWED: Record<string, string> = { '\b': 'b', '\f': 'f', '\n': 'n', '\r': 'r', '\t': 't' };
/** 줄바꿈 뒤 글자가 이 명령일 때만 명령으로 되돌린다. 그 밖의 줄바꿈은 글의 줄바꿈이다 */
const N_COMMANDS = ['neq', 'nabla', 'neg', 'not', 'notin', 'nmid', 'nleq', 'ngeq'];
const isAsciiLetter = (c: string) => (c >= 'a' && c <= 'z') || (c >= 'A' && c <= 'Z');

/** 파싱한 JSON 값의 문자열에서 삼킨 LaTeX 명령을 되돌린다(\frac 이 폼피드와 rac 로 갈라진 것 등). 모델 출력을 파싱한 직후 한 곳에서 쓴다 */
export function restoreEscapes(value: unknown): unknown {
  if (typeof value === 'string') {
    let out = '';
    for (let i = 0; i < value.length; i += 1) {
      const letter = SWALLOWED[value[i]];
      if (letter === undefined) { out += value[i]; continue; }
      let end = i + 1;
      while (end < value.length && isAsciiLetter(value[end])) end += 1;
      const command = letter === 'b' || letter === 'f' || (letter === 'n' ? N_COMMANDS.includes(letter + value.slice(i + 1, end)) : end > i + 1);
      out += command ? `\\${letter}` : value[i];
    }
    return out;
  }
  if (Array.isArray(value)) return value.map(restoreEscapes);
  if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, restoreEscapes(v)]));
  return value;
}

export interface ReadValues {
  /** 정규 이름 → 값 */
  values: Record<string, number>;
  /** 이름은 있으나 값을 정하지 못한(null) 기호 */
  unset: string[];
  /** 읽지 못해 버린 선언의 사유 */
  issues: string[];
}

/** 값 선언을 읽는다. 풀이 계약의 객체({alpha: 2, t: null})와 도구 선택 계약의 배열([{name, value}]) 둘 다. 버리는 것은 사유를 남긴다. known 은 canonicalName 의 것 */
export function readValues(raw: unknown, known: readonly string[] = []): ReadValues {
  const out: ReadValues = { values: {}, unset: [], issues: [] };
  let entries: [unknown, unknown][] = [];
  if (Array.isArray(raw)) {
    entries = raw.map((item) => {
      const one = item && typeof item === 'object' ? item as { name?: unknown; value?: unknown } : {};
      return [one.name, one.value];
    });
  } else if (raw && typeof raw === 'object') {
    entries = Object.entries(raw as Record<string, unknown>);
  }
  for (const [name, value] of entries) {
    if (typeof name !== 'string') { out.issues.push('이름이 글자가 아닌 값 선언을 버렸다'); continue; }
    const canon = canonicalName(name, known);
    if (!canon) { out.issues.push(`기호 "${name}" 을 엔진이 기호로 읽지 못한다(상수나 함수로 읽히거나 식이다)`); continue; }
    if (NOT_SYMBOLS.includes(canon)) { out.issues.push(`${canon} 는 변수나 함수 이름이라 값을 줄 수 없다`); continue; }
    if (value === null || value === undefined) { if (!out.unset.includes(canon)) out.unset.push(canon); continue; }
    if (typeof value !== 'number' || !Number.isFinite(value)) { out.issues.push(`기호 ${canon} 의 값 ${JSON.stringify(value)} 은 유한한 수가 아니다`); continue; }
    if (canon in out.values && out.values[canon] !== value) out.issues.push(`기호 ${canon} 의 값이 둘이다(${out.values[canon]}, ${value}). 뒤의 것을 쓴다`);
    out.values[canon] = value;
  }
  out.unset = out.unset.filter((n) => !(n in out.values));
  return out;
}

/** 모델 글의 이상 문자 정리(실측 2026-10-19 데모 qwen 출력: 수학 알파벳 𝑥 𝑓, 불가시 문자 U+2061 U+200B, 유니코드 기호 − ∣ ⋅ ⟹, 전각).
 * 한글, ASCII, LaTeX 는 그대로 두고 그 밖을 ASCII 와 LaTeX 로 되돌린다. 모델이 쓴 글(풀이, 단계 설명)에만 쓴다 */
const INVISIBLE = new Set([0x200b, 0x200c, 0x200d, 0x200e, 0x200f, 0x2060, 0x2061, 0x2062, 0x2063, 0x2064, 0xfeff]);
/** 기호 → LaTeX. 수식($…$) 안에서는 명령 그대로, 밖에서는 $ 로 감싼다. '−' '∣' 는 ASCII 로, 가운뎃점은 밖에서 쉼표로 */
const SYMBOL_LATEX: Record<string, string> = { '⋅': '\\cdot', '⟹': '\\Rightarrow', '≤': '\\le', '≥': '\\ge', '≠': '\\ne', '×': '\\times', '÷': '\\div', '√': '\\sqrt', '∞': '\\infty', '→': '\\rightarrow' };
const SYMBOL_ASCII: Record<string, string> = { '−': '-', '∣': '|' };
export function cleanModelText(text: string): string {
  let out = '';
  let inMath = false;
  for (const ch of String(text ?? '')) {
    const code = ch.codePointAt(0) ?? 0;
    if (ch === '$') { inMath = !inMath; out += ch; continue; }
    if (INVISIBLE.has(code) || (code < 32 && ch !== '\n' && ch !== '\t')) continue;
    if (code >= 0x1d400 && code <= 0x1d7ff) { const plain = ch.normalize('NFKC'); out += plain === ch ? '?' : plain; continue; }
    if (code >= 0xff01 && code <= 0xff5e) { out += String.fromCharCode(code - 0xfee0); continue; }
    if (SYMBOL_ASCII[ch] !== undefined) { out += SYMBOL_ASCII[ch]; continue; }
    if (ch === '·') { out += inMath ? '\\cdot ' : ', '; continue; }
    if (SYMBOL_LATEX[ch] !== undefined) { out += inMath ? `${SYMBOL_LATEX[ch]} ` : `$${SYMBOL_LATEX[ch]}$`; continue; }
    if ((code >= 0xe000 && code <= 0xf8ff) || code === 0xfffd) { out += '?'; continue; }
    if (ch === '°') { out += inMath ? '^\\circ ' : '$^\\circ$'; continue; }
    out += ch;
  }
  return out.split('$').map((seg, i) => (i % 2 === 1 ? visibleNotation(seg) : seg)).join('$');
}

/** 수식($…$) 안의 표기를 기출 발문의 표기로 맞춘다(사용자 지시 2026-10-21: 기호를 눈에 보이게). 수의 곱은 \\times, 벡터 사이의 \\cdot 은 내적이므로 둔다 */
const VECTOR_BEFORE = /\\(?:overrightarrow|vec|mathbf)\s*\{[^{}]*\}\s*$/;
const VECTOR_AFTER = /^\s*\\(?:overrightarrow|vec|mathbf)\b/;
export function visibleNotation(math: string): string {
  let out = '';
  let rest = math;
  for (;;) {
    const at = rest.search(/\\cdot(?![a-z])|\*/);
    if (at < 0) { out += rest; break; }
    const len = rest[at] === '*' ? 1 : 5;
    const before = out + rest.slice(0, at);
    const after = rest.slice(at + len);
    const vector = rest[at] !== '*' && (VECTOR_BEFORE.test(before) || VECTOR_AFTER.test(after));
    out = before + (vector ? '\\cdot ' : '\\times ');
    rest = after;
  }
  return out
    .replace(/<=/g, '\\le ')
    .replace(/>=/g, '\\ge ')
    .replace(/!=/g, '\\neq ')
    .replace(/(?<![\\A-Za-z])(sin|cos|tan|ln|log|pi|theta|alpha|beta|infty|sqrt)(?![A-Za-z])/g, '\\$1')
    .replace(/\\sqrt\(([^()]*)\)/g, '\\sqrt{$1}')
    .replace(/(?<![\w\\}])(\d+|[A-Za-z])\/(\d+|[A-Za-z])(?![\w{])/g, '\\frac{$1}{$2}');
}
